// ═══════════════════════════════════════════════════════════════
//  COO Operations Dashboard
//  • Workforce headcount vs planned (bar)
//  • CapEx utilization progress bars
//  • Department spend heatmap (month × dept)
//  • Operational efficiency metrics
//  • Cost per unit trend
// ═══════════════════════════════════════════════════════════════
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer, Cell, RadialBarChart, RadialBar,
} from "recharts";
import HeatmapGrid   from "../common/HeatmapGrid";
import LoaderComponent from "../../../../libraries/loader/loader";
import { PBExecuteQuery, Q } from "../../../../services/process/PlanningBudgeting/planningBudgetingService";

const SECTION = ({ title, children }) => (
  <div style={{
    background: "#fff", borderRadius: 14,
    boxShadow: "0 2px 14px rgba(73,105,171,0.09)",
    padding: "18px 20px", marginBottom: 22,
  }}>
    <div style={{ fontWeight: 700, fontSize: 15, color: "#1a2340", marginBottom: 14 }}>{title}</div>
    {children}
  </div>
);

// ── Circular progress meter ─────────────────────────────────────
const UtilMeter = ({ label, pct, color }) => (
  <div style={{ textAlign: "center", minWidth: 100 }}>
    <div style={{ position: "relative", width: 80, height: 80, margin: "0 auto" }}>
      <svg viewBox="0 0 36 36" style={{ transform: "rotate(-90deg)" }}>
        <circle cx="18" cy="18" r="15.9" fill="none" stroke="#f0f2f8" strokeWidth="3.5" />
        <circle cx="18" cy="18" r="15.9" fill="none"
          stroke={pct > 100 ? "#c0392b" : pct > 85 ? "#e67e22" : color}
          strokeWidth="3.5"
          strokeDasharray={`${Math.min(pct, 100)} ${100 - Math.min(pct, 100)}`}
          strokeLinecap="round"
        />
      </svg>
      <div style={{
        position: "absolute", top: "50%", left: "50%",
        transform: "translate(-50%,-50%)",
        fontSize: 12, fontWeight: 800,
        color: pct > 100 ? "#c0392b" : pct > 85 ? "#e67e22" : color,
      }}>{pct}%</div>
    </div>
    <div style={{ fontSize: 10, color: "#888", marginTop: 6, maxWidth: 100 }}>{label}</div>
  </div>
);

// ── CapEx project progress bar ─────────────────────────────────
const CapExRow = ({ project, assetType, approved, spent, pct, status }) => {
  const barColor = pct > 100 ? "#c0392b" : pct > 85 ? "#e67e22" : "#4969ab";
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: "#333" }}>{project}</div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span style={{ fontSize: 10, color: "#aaa" }}>{assetType}</span>
          <span style={{
            fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 12,
            background: status === "Approved" ? "#e8f5ee" : status === "On Hold" ? "#fff3e0" : "#f0f2f8",
            color:      status === "Approved" ? "#2d8c4e" : status === "On Hold" ? "#e67e22" : "#888",
          }}>{status}</span>
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ flex: 1, background: "#f0f2f8", borderRadius: 6, height: 10, overflow: "hidden" }}>
          <div style={{
            width: `${Math.min(pct, 100)}%`, height: 10,
            background: barColor, borderRadius: 6, transition: "width 0.6s ease",
          }} />
        </div>
        <div style={{ fontSize: 11, fontWeight: 700, color: barColor, width: 44, textAlign: "right" }}>{pct}%</div>
        <div style={{ fontSize: 10, color: "#aaa", whiteSpace: "nowrap" }}>
          ₹{Number(spent).toLocaleString("en-IN")} / ₹{Number(approved).toLocaleString("en-IN")}
        </div>
      </div>
    </div>
  );
};

export default function COODashboard() {
  const [year,      setYear]      = useState(new Date().getFullYear());
  const [loading,   setLoading]   = useState(true);
  const [workforce, setWorkforce] = useState([]);
  const [capex,     setCapex]     = useState([]);
  const [heatmap,   setHeatmap]   = useState([]);

  const [userCredentials] = useState(
    JSON.parse(sessionStorage.getItem("userCredentials") || "{}")
  );

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const c = PBExecuteQuery();
      const [wfRes, cxRes, hmRes] = await Promise.all([
        axios.post(c[1], { query: Q.workforceByDept(userCredentials.selectedentity, year) }, c[0]),
        axios.post(c[1], { query: Q.capexUtilization(userCredentials.selectedentity, year) }, c[0]),
        axios.post(c[1], { query: Q.deptSpendHeatmap(userCredentials.selectedentity, year) }, c[0]),
      ]);
      setWorkforce(wfRes.data || []);
      setCapex(cxRes.data     || []);
      setHeatmap(hmRes.data   || []);
    } catch (e) {
      console.error("COO fetch error", e);
    } finally {
      setLoading(false);
    }
  }, [year, userCredentials.selectedentity]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Derived ──────────────────────────────────────────────────
  const totalCurrentHC  = workforce.reduce((s, r) => s + Number(r.current_hc || 0), 0);
  const totalPlannedHC  = workforce.reduce((s, r) => s + Number(r.planned_hc || 0), 0);
  const totalWFCost     = workforce.reduce((s, r) => s + Number(r.total_cost  || 0), 0);
  const totalCapexApprv = capex.reduce((s, r) => s + Number(r.approved_amount || 0), 0);
  const totalCapexSpent = capex.reduce((s, r) => s + Number(r.spent_amount    || 0), 0);
  const overallCapexPct = totalCapexApprv ? Math.round((totalCapexSpent / totalCapexApprv) * 100) : 0;
  const hcFillRate      = totalPlannedHC  ? Math.round((totalCurrentHC  / totalPlannedHC)  * 100) : 0;

  if (loading) return <LoaderComponent loaderOpen />;

  return (
    <div style={{ padding: "6px 2px" }}>

      {/* ── Header ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 10 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 800, color: "#1a2340" }}>COO Operations Dashboard</div>
          <div style={{ fontSize: 12, color: "#aaa" }}>Workforce, CapEx & operational efficiency · FY {year}</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <select value={year} onChange={(e) => setYear(Number(e.target.value))}
            style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid #d0d8ec", fontSize: 13 }}>
            {[2023, 2024, 2025, 2026].map((y) => <option key={y}>{y}</option>)}
          </select>
          <button onClick={fetchAll} style={{
            padding: "6px 14px", borderRadius: 8, background: "#4969ab",
            color: "#fff", border: "none", fontSize: 12, cursor: "pointer", fontWeight: 600,
          }}>↻ Refresh</button>
        </div>
      </div>

      {/* ── Ops KPI summary ── */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 20 }}>
        {[
          { label: "Total Headcount",    value: totalCurrentHC.toLocaleString("en-IN"),  color: "#4969ab" },
          { label: "Planned HC",         value: totalPlannedHC.toLocaleString("en-IN"),  color: "#7b68ee" },
          { label: "HC Fill Rate",       value: `${hcFillRate}%`,    color: hcFillRate  >= 90 ? "#2d8c4e" : "#e67e22" },
          { label: "Total WF Cost",      value: `₹${(totalWFCost/10000000).toFixed(2)}Cr`, color: "#c0392b" },
          { label: "CapEx Approved",     value: `₹${(totalCapexApprv/10000000).toFixed(2)}Cr`, color: "#2d8c4e" },
          { label: "CapEx Utilization",  value: `${overallCapexPct}%`, color: overallCapexPct > 100 ? "#c0392b" : "#2d8c4e" },
        ].map(({ label, value, color }) => (
          <div key={label} style={{
            background: `${color}10`, border: `1px solid ${color}30`,
            borderRadius: 10, padding: "10px 16px", minWidth: 130,
          }}>
            <div style={{ fontSize: 10, color: "#999", textTransform: "uppercase", letterSpacing: 0.6 }}>{label}</div>
            <div style={{ fontWeight: 800, fontSize: 18, color }}>{value}</div>
          </div>
        ))}
      </div>

      {/* ── Workforce + utilization meters ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 22 }}>

        {/* Workforce bar */}
        <SECTION title="👥 Workforce: Current vs Planned Headcount">
          {workforce.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={workforce} margin={{ top: 5, right: 10, left: 10, bottom: 50 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f2f8" />
                <XAxis dataKey="department" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" interval={0} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend iconSize={9} wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="current_hc" name="Current HC" fill="#4969ab" radius={[4,4,0,0]} />
                <Bar dataKey="planned_hc" name="Planned HC" fill="#7b68ee" radius={[4,4,0,0]} opacity={0.6} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ textAlign: "center", color: "#bbb", padding: 40 }}>No workforce data</div>
          )}
        </SECTION>

        {/* Dept utilization meters */}
        <SECTION title="⚡ Department Utilization Rate">
          <div style={{ display: "flex", flexWrap: "wrap", gap: 16, justifyContent: "center" }}>
            {workforce.slice(0, 8).map((r) => (
              <UtilMeter
                key={r.department}
                label={r.department}
                pct={Math.round(Number(r.utilization || 0))}
                color="#4969ab"
              />
            ))}
            {workforce.length === 0 && (
              <div style={{ color: "#bbb", padding: 40 }}>No utilization data</div>
            )}
          </div>
        </SECTION>
      </div>

      {/* ── CapEx Project Progress ── */}
      <SECTION title="🏗️ CapEx Project Utilization">
        {capex.length > 0 ? (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 32px" }}>
            {capex.map((p) => (
              <CapExRow
                key={p.project_name}
                project={p.project_name}
                assetType={p.asset_type}
                approved={p.approved_amount}
                spent={p.spent_amount}
                pct={Math.round(Number(p.utilization_pct || 0))}
                status={p.status}
              />
            ))}
          </div>
        ) : (
          <div style={{ textAlign: "center", color: "#bbb", padding: 40 }}>No CapEx data for FY {year}</div>
        )}
      </SECTION>

      {/* ── Department Spend Heatmap ── */}
      {heatmap.length > 0 ? (
        <HeatmapGrid
          data={heatmap}
          title="🌡️ Department Spend Utilization Heatmap (Actual % of Budget)"
        />
      ) : (
        <SECTION title="🌡️ Department Spend Heatmap">
          <div style={{ textAlign: "center", color: "#bbb", padding: 40 }}>No heatmap data for FY {year}</div>
        </SECTION>
      )}

      <div style={{ fontSize: 10, color: "#ccc", textAlign: "right", marginTop: 12 }}>
        Last refreshed: {new Date().toLocaleString()} · FY {year}
      </div>
    </div>
  );
}
